import { SimpleGrid, FormControl, Box, Text, Radio, Flex, FormErrorMessage } from '@chakra-ui/react';
import { FormattedMessage, useIntl } from 'react-intl';

import DateField from '../forms/DateField';
import NumberInputField from '../forms/NumberInputField';
import CustomCheckbox from '../forms/CustomCheckbox';
import RadioGroupField from '../forms/RadioGroupField';
import ComboboxField from '../forms/ComboboxField';
import Error from '../utils/Error';

import { ReactComponent as WarningIcon } from '../../icons/warning.svg';
import { RT_PRICE_SENSITIVITY } from '../../constants/studies';
import InfoPopover from 'components/forms/InfoPopover';

const priorityOptions = [
    { id: 'Low', description: 'Low' },
    { id: 'Normal', description: 'Normal' },
    { id: 'High', description: 'High' },
    { id: 'Urgent', description: 'Urgent' },
];

const reportOptions = [
    { id: 'PTDPAYOFF', description: 'Prior to Delivery Payoffs Reports' },
    { id: 'SIMREPORTS', description: 'Simulation Validation Reports' },
];

const SHOW_ALL_PEAK_PERIODS_OPTION_ID = 'SHOW_ALL_PEAK_PERIODS_OPTION_ID';

const SettingsTab = ({
    settings,
    toggleSettings,
    selectedPeakPeriods,
    selectedSensitivity,
    selectedReports,
    selectedTimeIntervals,
    runDate,
    setRunDate,
    isRunReportsNowChecked,
    onIsRunReportsNowChanged,
    isDeterministicChecked,
    onIsDeterministicChanged,
    isFastModeChecked,
    isFastModeDisabled,
    onIsFastModeChanged,
    timeStepper,
    setTimeStepper,
    priority,
    setPriority,
    curvePrice,
    setCurvePrice,
    curveSource,
    setCurveSource,
    simulations,
    setSimulations,
    foSimulations,
    setFOSimulations,
    isFOSimulationsFieldVisible,
    errors,
    studyType,
    setStudyType,
    isDoNotDeleteChecked,
    onIsDoNotDeleteChanged,
    isTemplateChecked,
    onIsTemplateChanged,
    peakPeriodTypes,
    selectedPeakPeriodTypes,
    setSelectedPeakPeriodTypes,
    loadingError,
}) => {
    const intl = useIntl();
    const allPeakPeriodsIds = settings?.peakPeriods?.map((c) => c.id);

    const getPeakPeriodTypeOption = (periodTypeId, periodTypeDescr, peakPeriodsIds) => {
        const someSelected = peakPeriodsIds?.some((d) => selectedPeakPeriods?.includes(d));
        const noneSelected = !someSelected;
        const allSelected = peakPeriodsIds?.every((d) => selectedPeakPeriods?.includes(d));
        //Add * to the end of description that has data as in hedge instruments " *"
        const suffix = someSelected ? intl.formatMessage({ id: 'run_a_study_settings_peak_period_has_selected' }) : '';

        return {
            id: periodTypeId,
            description: periodTypeDescr + suffix,
            someSelected,
            allSelected,
            noneSelected,
        };
    };

    const showAllPeakPeriodsOption = getPeakPeriodTypeOption(
        SHOW_ALL_PEAK_PERIODS_OPTION_ID,
        intl.formatMessage({ id: 'run_a_study_settings_peak_period_show_all_option' }),
        allPeakPeriodsIds
    );

    // Create peak periods types drop down filter options
    // When peak period type is selected, only it's peak period labels will be visible for selection
    const peakPeriodTypesOptions = peakPeriodTypes?.map((periodTypeDescr) => {
        const peakPeriodsIds = settings?.peakPeriods
            ?.filter((pp) => pp.peakPeriodType === periodTypeDescr)
            ?.map((c) => c.id);
        return getPeakPeriodTypeOption(periodTypeDescr, periodTypeDescr, peakPeriodsIds);
    });

    // Add show all option to the top of peak periods filter list to display in drop down
    peakPeriodTypesOptions.unshift(showAllPeakPeriodsOption);

    // If we did not find any peak period types/groups to select/set as default option then select show all option
    const selectedPeriodType =
        Array.isArray(selectedPeakPeriodTypes) && selectedPeakPeriodTypes.length > 0
            ? selectedPeakPeriodTypes[0]
            : SHOW_ALL_PEAK_PERIODS_OPTION_ID;

    const selectedPeakperiodTypeOption = peakPeriodTypesOptions?.find((po) => po.id === selectedPeriodType);

    const visiblePeakPeriodsInfo =
        selectedPeriodType === SHOW_ALL_PEAK_PERIODS_OPTION_ID
            ? settings?.peakPeriods // show all peak period labels checkboxes
            : settings?.peakPeriods?.filter((pp) => pp.peakPeriodType === selectedPeriodType);

    if (loadingError) {
        return (
            <Box mt={6}>
                <Error primaryId="common_error" secondaryId="common_loading_error" additionalText="study settings" />
            </Box>
        );
    }

    return (
        <>
            <Box mb={5} pb={5} borderBottom="1px solid" borderColor="border-secondary">
                <SimpleGrid columns={{ base: 1, lg: 2 }} gap={4}>
                    <Flex
                        gridGap={4}
                        pr={4}
                        borderRight="1px solid"
                        borderColor={{ base: 'transparent', lg: 'border-secondary' }}
                    >
                        <Box width="2xs">
                            <ComboboxField
                                mb={0}
                                id="studyType"
                                name="studyType"
                                label={intl.formatMessage({ id: 'run_a_study_settings_study_type' })}
                                options={settings.studyTypes}
                                valueKey="id"
                                labelKey="description"
                                value={studyType}
                                onChange={setStudyType}
                            />
                        </Box>
                        {/* <Flex align="flex-end">  */}
                        <Flex align="center">
                            <CustomCheckbox isChecked={isTemplateChecked} onChange={onIsTemplateChanged.toggle}>
                                <FormattedMessage id="run_a_study_settings_is_a_template" />
                            </CustomCheckbox>
                        </Flex>
                        {/* <Flex align="flex-end">  */}
                        <Flex align="center">
                            <CustomCheckbox isChecked={isDoNotDeleteChecked} onChange={onIsDoNotDeleteChanged.toggle}>
                                <FormattedMessage id="run_a_study_settings_do_not_delete" />
                            </CustomCheckbox>
                        </Flex>
                    </Flex>

                    <SimpleGrid columns={3} gap={4} pl={{ base: 0, lg: 2 }}>
                        <ComboboxField
                            mb={0}
                            id="timeStepSize"
                            name="timeStepSize"
                            label={intl.formatMessage({ id: 'run_a_study_settings_time_step' })}
                            options={settings.timeStepSize}
                            valueKey="id"
                            labelKey="description"
                            value={timeStepper}
                            onChange={setTimeStepper}
                        />

                        <NumberInputField
                            id="numberOfSimulations"
                            name="numberOfSimulations"
                            label={intl.formatMessage({ id: 'run_a_study_settings_simulations' })}
                            placeholder={intl.formatMessage({ id: 'common_select_number' })}
                            min={1}
                            precision={0}
                            isDisabled={isDeterministicChecked}
                            value={isDeterministicChecked ? 1 : simulations}
                            onChange={(e) => setSimulations(e)}
                        />

                        {isFOSimulationsFieldVisible && (
                            <NumberInputField
                                id="numberOfFOSimulations"
                                name="numberOfFOSimulations"
                                label={intl.formatMessage({ id: 'run_a_study_settings_fo_simulations' })}
                                placeholder={intl.formatMessage({ id: 'common_select_number' })}
                                min={1}
                                precision={0}
                                isDisabled={isDeterministicChecked}
                                value={isDeterministicChecked ? 1 : foSimulations}
                                onChange={(e) => setFOSimulations(e)}
                            />
                        )}
                        {/* Deterministic checkbox disables both sim and FO sim counts and sends "1" to the database.  */}
                        <Box alignSelf="flex-start">
                            <Flex direction="column" align="flex-start">
                                <CustomCheckbox
                                    isChecked={isDeterministicChecked}
                                    onChange={onIsDeterministicChanged}
                                    mt={3}
                                >
                                    <FormattedMessage id="run_a_study_settings_deterministic" />
                                </CustomCheckbox>
                                {
                                    <Flex gap={2} alignItems={'center'}>
                                        <CustomCheckbox
                                            isChecked={isFastModeChecked}
                                            isDisabled={isFastModeDisabled}
                                            onChange={onIsFastModeChanged}
                                            mt={1}
                                        >
                                            <FormattedMessage
                                                id="run_a_study_settings_fast_mode"
                                                defaultMessage="Fast Mode"
                                            />
                                        </CustomCheckbox>
                                        <InfoPopover
                                            info={<FormattedMessage id="run_a_study_settings_fast_mode_info" />}
                                        />
                                    </Flex>
                                }
                            </Flex>
                        </Box>
                    </SimpleGrid>
                </SimpleGrid>
            </Box>
            <Box mb={5} pb={10} borderBottom="1px solid" borderColor="border-secondary">
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
                    <Box gridColumnStart="1" gridColumnEnd="2">
                        <Text mb={3}>
                            <FormattedMessage id="run_a_study_settings_run_schedule" />
                        </Text>
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
                            <Box mb="40px">
                                <DateField
                                    mb={5}
                                    maxWidth="300px"
                                    id="scheduleStartDate"
                                    name="scheduleStartDate"
                                    label={intl.formatMessage({ id: 'run_a_study_settings_date_and_time' })}
                                    isDisabled={isRunReportsNowChecked}
                                    value={runDate}
                                    onChange={(date) => setRunDate(date)}
                                    utc={false}
                                />

                                <CustomCheckbox
                                    isChecked={isRunReportsNowChecked}
                                    onChange={onIsRunReportsNowChanged.toggle}
                                >
                                    <FormattedMessage id="run_a_study_settings_date_picker_run_now" />
                                </CustomCheckbox>
                            </Box>
                        </SimpleGrid>
                    </Box>
                    <Box>
                        <Text mb={3}>
                            <FormattedMessage id="run_a_study_settings_curve_settings" />
                        </Text>

                        <ComboboxField
                            mb={3}
                            id="curvePrice"
                            name="curvePrice"
                            label={intl.formatMessage({ id: 'run_a_study_settings_curve_price' })}
                            options={settings.curvePriceTypes}
                            valueKey="id"
                            labelKey="description"
                            value={curvePrice}
                            onChange={setCurvePrice}
                        />

                        <ComboboxField
                            mb={3}
                            id="curveSource"
                            name="curveSource"
                            label={intl.formatMessage({ id: 'run_a_study_settings_curve_source' })}
                            options={settings.curveSources}
                            valueKey="id"
                            labelKey="description"
                            value={curveSource}
                            onChange={setCurveSource}
                        />
                    </Box>
                </SimpleGrid>

                <RadioGroupField
                    label={<FormattedMessage id="run_a_study_settings_priority" />}
                    name="settingsPriority"
                    value={priority}
                    onChange={setPriority}
                >
                    {priorityOptions.map(({ id, description }) => (
                        <Radio key={id} value={id} mr={5}>
                            {description}
                        </Radio>
                    ))}
                </RadioGroupField>
            </Box>
            <FormControl id="options">
                <Box mb={5} pb={10} borderBottom="1px solid" borderColor="border-secondary">
                    <Text mb={3}>
                        <FormattedMessage id="run_a_study_settings_report_requirements" />
                    </Text>
                    <SimpleGrid columns={{ base: 1, md: 4 }} spacing={5}>
                        {reportOptions.map(({ id, description }) => (
                            <CustomCheckbox
                                key={id}
                                isChecked={selectedReports.includes(id)}
                                onChange={() => toggleSettings(id, 'report')}
                            >
                                {description}
                            </CustomCheckbox>
                        ))}
                    </SimpleGrid>
                </Box>

                <Box mb={5} pb={10} borderBottom="1px solid" borderColor="border-secondary">
                    <FormControl isInvalid={errors.timeIntervals}>
                        <Flex mb={3} align="center">
                            <Text display="flex" align="center">
                                <FormattedMessage id="run_a_study_settings_time_intervals" />
                            </Text>
                            {errors.timeIntervals && (
                                <Flex ml={2} gridGap={3}>
                                    <WarningIcon />
                                    <FormErrorMessage mt={0}>{errors.timeIntervals}</FormErrorMessage>
                                </Flex>
                            )}
                        </Flex>
                        <SimpleGrid columns={{ base: 1, md: 4 }} spacing={5}>
                            {settings.dateIntervals.map(({ id, description }) => (
                                <CustomCheckbox
                                    key={id}
                                    isChecked={selectedTimeIntervals.includes(id)}
                                    onChange={() => toggleSettings(id, 'time')}
                                >
                                    {description}
                                </CustomCheckbox>
                            ))}
                        </SimpleGrid>
                    </FormControl>
                </Box>

                <Box mb={5} pb={10} borderBottom="1px solid" borderColor="border-secondary">
                    <FormControl isInvalid={errors.peakPeriods}>
                        <Flex mb={3} align="center">
                            <Text display="flex" align="center">
                                <FormattedMessage id="run_a_study_settings_peak_periods" />
                            </Text>
                            {errors.peakPeriods && (
                                <Flex ml={2} gridGap={3}>
                                    <WarningIcon />
                                    <FormErrorMessage mt={0}>{errors.peakPeriods}</FormErrorMessage>
                                </Flex>
                            )}
                        </Flex>

                        <SimpleGrid mb={5} columns={{ base: 1, md: 2 }} spacing={5}>
                            <ComboboxField
                                id="peakPeriodTypes"
                                name="peakPeriodTypes"
                                label={intl.formatMessage({ id: 'run_a_study_settings_peak_period_type' })}
                                placeholderLabel={intl.formatMessage({
                                    id: 'run_a_study_select_placeholder',
                                })}
                                options={peakPeriodTypesOptions}
                                valueKey="id"
                                labelKey="description"
                                onChange={(val) => setSelectedPeakPeriodTypes([val])}
                                value={selectedPeriodType}
                                info={<FormattedMessage id="run_a_study_settings_peak_period_type_popover" />}
                            />
                            <SimpleGrid mt={5} columns={{ base: 1, md: 2 }} spacing={5}>
                                <CustomCheckbox
                                    key={'select_deselect_all'}
                                    isChecked={selectedPeakperiodTypeOption?.allSelected}
                                    onChange={(event) =>
                                        toggleSettings(
                                            selectedPeriodType,
                                            selectedPeakperiodTypeOption?.allSelected
                                                ? 'unselectAllPeakPeriodType'
                                                : 'selectAllPeakPeriodType'
                                        )
                                    }
                                >
                                    {intl.formatMessage({ id: 'common_select_all' })}
                                </CustomCheckbox>
                            </SimpleGrid>
                        </SimpleGrid>

                        <SimpleGrid columns={{ base: 1, md: 4 }} spacing={5}>
                            {visiblePeakPeriodsInfo?.map(({ id, description }) => (
                                <CustomCheckbox
                                    key={id}
                                    isChecked={selectedPeakPeriods.includes(id)}
                                    onChange={(event) => toggleSettings(id, 'peak')}
                                >
                                    {description}
                                </CustomCheckbox>
                            ))}
                        </SimpleGrid>
                    </FormControl>
                </Box>

                <Box mb={5} pb={10} borderBottom="1px solid" borderColor="border-secondary">
                    <FormControl isInvalid={errors.sensitivity}>
                        <Flex mb={3} align="center">
                            <Text display="flex" align="center">
                                <FormattedMessage id="run_a_study_settings_sensitivity" />
                            </Text>
                            {errors.sensitivity && (
                                <Flex ml={2} gridGap={3}>
                                    <WarningIcon />
                                    <FormErrorMessage mt={0}>{errors.sensitivity}</FormErrorMessage>
                                </Flex>
                            )}
                        </Flex>

                        <SimpleGrid columns={{ base: 1, md: 4 }} spacing={5}>
                            {settings.sensitivities
                                .filter(
                                    ({ description }) =>
                                        description.toLowerCase() !== RT_PRICE_SENSITIVITY.FAST_MODE.description &&
                                        description.toLowerCase() !==
                                            RT_PRICE_SENSITIVITY.DETERMINISTIC_MITIGATION.description
                                )
                                .map(({ id, description }) => (
                                    <CustomCheckbox
                                        key={id}
                                        isChecked={selectedSensitivity.includes(id)}
                                        onChange={(event) => toggleSettings(id, 'sensitivity')}
                                    >
                                        {description}
                                    </CustomCheckbox>
                                ))}
                        </SimpleGrid>
                    </FormControl>
                </Box>
            </FormControl>
        </>
    );
};
export default SettingsTab;
